import { useCallback, useEffect, useRef, useState } from "react";
import { useMyPermissions } from "../../lib/permissions.js";
import { denialOrRawMessage } from "../../lib/denialReason.js";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";
import {
    createCopilotIntegration,
    deleteCopilotIntegration,
    getCopilotAuthUrl,
    updateCopilotIntegration,
} from "../../lib/copilotClient.js";
import { directoryOauthRedirectUri } from "../../lib/oauthCallback.js";
import { copilotHoldsTenant } from "../../lib/microsoftTenant.js";
import { useDirectoryOauthPopup } from "../../lib/useDirectoryOauthPopup.js";
import CopiedMicrosoftAppNote, {
    COPY_TARGET,
    PermissionNames,
} from "../../components/identity/CopiedMicrosoftAppNote.jsx";
import {
    BYO_COPILOT_PERMISSION_GROUPS,
    COPILOT_SHARED_WITH_DIRECTORY,
    MANAGED_MICROSOFT_PERMISSION_GROUPS,
    MANAGED_MICROSOFT_PERMISSIONS_NOTE,
} from "../../components/identity/microsoftPermissions.js";
import {
    NO_DIRECTORY,
    connectedEntraDirectories,
    copilotConnectionName,
    copilotNeedsAttention,
    copilotProblem,
    copilotSteps,
    deliveryDelayNote,
    scopeCaptureStep,
    whenLabel,
} from "./model.js";
import {
    AsideSection,
    ConsentWait,
    ErrorText,
    Field,
    MethodOption,
    Panel,
    PermissionList,
    SourceLoadState,
    SourcePage,
    Step,
    Steps,
    Tabs,
    inputCls,
    linkCls,
} from "../../components/setupSteps.jsx";
import { TONE, dangerBtnCls, primaryBtnCls, secondaryBtnCls } from "../../components/statusCard.jsx";

const NEW_TAB = "new";
const CONSENT_PROVIDER = "COPILOT";
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const POPUP_BLOCKED = "Your browser blocked the consent tab. Allow popups for this site, then try again.";

export const CONNECT_METHOD = {
    ENTRA: "entra",
    MANAGED: "managed",
    BYO: "byo",
};

function connectionTone(integration) {
    if (copilotNeedsAttention(integration)) return TONE.ATTENTION;
    return integration.status === "connected" ? TONE.OK : TONE.WAITING;
}

function ManagedPermissionList() {
    return (
        <PermissionList
            title="Permissions Origin's app requests (all read-only)"
            groups={MANAGED_MICROSOFT_PERMISSION_GROUPS}
            note={MANAGED_MICROSOFT_PERMISSIONS_NOTE}
        />
    );
}

function ByoPermissionList() {
    return (
        <PermissionList
            title="Permissions to add to your app registration (all read-only)"
            groups={BYO_COPILOT_PERMISSION_GROUPS}
            note={
                <>
                    Add them as Application permissions, not Delegated, then grant admin consent.{" "}
                    <PermissionNames permissions={COPILOT_SHARED_WITH_DIRECTORY} /> are also in the directory sync set. If one app
                    registration serves both connections, grant them once. Two separate registrations each need
                    them.
                </>
            }
        />
    );
}

function EntraCopyForm({ directories, onCreated }) {
    const caps = useMyPermissions();
    const copyable = directories.filter((candidate) => !candidate.copilotConnected);
    const [selectedId, setSelectedId] = useState(copyable[0]?.id ?? null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const directory = copyable.find((candidate) => candidate.id === selectedId) ?? copyable[0];

    const connect = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            await createCopilotIntegration({ copyFromDirectoryIntegrationId: directory.id });
            await onCreated();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [caps, directory, onCreated]);

    return (
        <Panel className="grid gap-[12px] p-[14px]">
            {directories.length > 1 ? (
                <fieldset className="m-0 grid gap-[6px] border-0 p-0">
                    <legend className="mb-[4px] text-[12px] font-medium text-[color:var(--ink)]">Entra connection</legend>
                    {directories.map((candidate) => (
                        <label
                            key={candidate.id}
                            title={candidate.copilotConnected ? COPILOT_ALREADY_CONNECTED : undefined}
                            className={`flex items-center gap-[8px] text-[12px] text-[color:var(--ink)] ${candidate.copilotConnected ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                        >
                            <input
                                type="radio"
                                name="copilot-entra-source"
                                checked={candidate.id === directory.id}
                                onChange={() => setSelectedId(candidate.id)}
                                disabled={candidate.copilotConnected}
                            />
                            <span className="font-mono">{candidate.displayName || "Entra ID directory"}</span>
                            {candidate.copilotConnected ? (
                                <span className="text-[11px] text-[color:var(--muted)]">Copilot already connected</span>
                            ) : null}
                        </label>
                    ))}
                </fieldset>
            ) : (
                <p className="m-0 text-[12px] text-[color:var(--ink)]">
                    Copies <span className="font-mono">{directory.displayName || "your Entra connection"}</span>.
                </p>
            )}
            <CopiedMicrosoftAppNote
                target={COPY_TARGET.COPILOT}
                managed={directory.managed}
                source="your Entra connection"
                className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]"
            />
            <div>
                <button type="button" onClick={connect} disabled={saving} className={primaryBtnCls}>
                    {saving ? "Connecting..." : "Connect"}
                </button>
            </div>
            <ErrorText>{error}</ErrorText>
        </Panel>
    );
}

function ManagedConnectForm({ onCreated }) {
    const caps = useMyPermissions();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const pendingIdRef = useRef(null);
    const onCreatedRef = useRef(onCreated);
    onCreatedRef.current = onCreated;

    const discardPending = useCallback(() => {
        const id = pendingIdRef.current;
        pendingIdRef.current = null;
        if (id) deleteCopilotIntegration(id).catch(() => {});
    }, []);

    const { pendingId, start, cancel } = useDirectoryOauthPopup({
        onResult: ({ status, message }) => {
            if (status === "success") {
                pendingIdRef.current = null;
                onCreatedRef.current();
                return;
            }
            discardPending();
            setError(message || "Microsoft did not complete admin consent.");
        },
        onClosedWithoutResult: () => {
            pendingIdRef.current = null;
            onCreatedRef.current();
        },
    });

    useEffect(() => discardPending, [discardPending]);

    const connect = useCallback(async () => {
        setSaving(true);
        setError(null);
        let newId = null;
        try {
            const created = await createCopilotIntegration({ managed: true });
            newId = created?.integration?.id ?? null;
            if (!newId) throw new Error("The connection was created but no ID came back. Try again.");
            const redirectUri = directoryOauthRedirectUri(window.location.origin);
            const { authUrl } = await getCopilotAuthUrl({ integrationId: newId, redirectUri });
            if (!start({ integrationId: newId, provider: CONSENT_PROVIDER, redirectUri, authUrl })) {
                await deleteCopilotIntegration(newId).catch(() => {});
                setError(POPUP_BLOCKED);
                return;
            }
            pendingIdRef.current = newId;
        } catch (err) {
            if (newId) await deleteCopilotIntegration(newId).catch(() => {});
            cancel();
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [caps, cancel, start]);

    const stop = useCallback(() => {
        cancel();
        discardPending();
    }, [cancel, discardPending]);

    return (
        <div className="grid gap-[12px]">
            <ManagedPermissionList />
            {pendingId ? (
                <ConsentWait onCancel={stop} />
            ) : (
                <div>
                    <button type="button" onClick={connect} disabled={saving} className={primaryBtnCls}>
                        {saving ? "Opening Microsoft..." : "Continue to Microsoft"}
                    </button>
                </div>
            )}
            <ErrorText>{error}</ErrorText>
        </div>
    );
}

function ClientIdField({ value, onChange, hint }) {
    return (
        <Field label="Application (client) ID" hint={hint}>
            <input
                type="text"
                value={value}
                spellCheck={false}
                onChange={(event) => onChange(event.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                className={inputCls}
            />
        </Field>
    );
}

function ClientSecretField({ value, onChange }) {
    return (
        <Field label="Client secret" hint="The secret's Value, not its ID. Stored encrypted and never shown again.">
            <input
                type="password"
                value={value}
                autoComplete="off"
                onChange={(event) => onChange(event.target.value)}
                className={inputCls}
            />
        </Field>
    );
}

function ByoConnectForm({ onCreated }) {
    const caps = useMyPermissions();
    const [tenantId, setTenantId] = useState("");
    const [clientId, setClientId] = useState("");
    const [clientSecret, setClientSecret] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const tenantValid = GUID.test(tenantId.trim());
    const clientValid = GUID.test(clientId.trim());
    const canConnect = tenantValid && clientValid && clientSecret.trim().length > 0 && !saving;

    const connect = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            await createCopilotIntegration({
                entraTenantId: tenantId.trim(),
                clientId: clientId.trim(),
                clientSecret: clientSecret.trim(),
            });
            setClientSecret("");
            await onCreated();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [caps, clientId, clientSecret, onCreated, tenantId]);

    return (
        <div className="grid gap-[12px]">
            <ByoPermissionList />
            <Panel className="grid gap-[12px] p-[14px]">
                <div className="grid gap-[12px] sm:grid-cols-2">
                    <Field
                        label="Directory (tenant) ID"
                        hint={tenantId.trim() && !tenantValid ? "Paste the GUID from the app's Overview page." : "On the app registration's Overview page."}
                    >
                        <input
                            type="text"
                            value={tenantId}
                            spellCheck={false}
                            onChange={(event) => setTenantId(event.target.value)}
                            placeholder="00000000-0000-0000-0000-000000000000"
                            className={inputCls}
                        />
                    </Field>
                    <ClientIdField
                        value={clientId}
                        onChange={setClientId}
                        hint={clientId.trim() && !clientValid ? "Paste the GUID from the app's Overview page." : "On the same Overview page."}
                    />
                </div>
                <ClientSecretField value={clientSecret} onChange={setClientSecret} />
                <div>
                    <button type="button" onClick={connect} disabled={!canConnect} className={primaryBtnCls}>
                        {saving ? "Checking with Microsoft..." : "Connect"}
                    </button>
                </div>
                <ErrorText>{error}</ErrorText>
            </Panel>
        </div>
    );
}

function EditConnectionForm({ integration, onSaved, onCancel }) {
    const caps = useMyPermissions();
    const [clientId, setClientId] = useState("");
    const [clientSecret, setClientSecret] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const clientInvalid = clientId.trim().length > 0 && !GUID.test(clientId.trim());
    const canSave = !clientInvalid && clientSecret.trim().length > 0 && !saving;

    const save = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            await updateCopilotIntegration(integration.id, {
                clientId: clientId.trim(),
                clientSecret: clientSecret.trim(),
            });
            setClientSecret("");
            await onSaved();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [caps, clientId, clientSecret, integration.id, onSaved]);

    return (
        <Panel className="grid gap-[12px] p-[14px]">
            <ClientIdField
                value={clientId}
                onChange={setClientId}
                hint={clientInvalid ? "Paste the GUID from the app's Overview page." : "Leave blank to keep the current one."}
            />
            <ClientSecretField value={clientSecret} onChange={setClientSecret} />
            <div className="flex flex-wrap items-center gap-[8px]">
                <button type="button" onClick={save} disabled={!canSave} className={primaryBtnCls}>
                    {saving ? "Checking with Microsoft..." : "Save connection"}
                </button>
                <button type="button" onClick={onCancel} className={secondaryBtnCls}>
                    Cancel
                </button>
            </div>
            <ErrorText>{error}</ErrorText>
        </Panel>
    );
}

const COPILOT_ALREADY_CONNECTED = "This Entra directory's Microsoft tenant already has a Copilot connection.";

const METHOD_STEP_TITLE = {
    [CONNECT_METHOD.ENTRA]: "Copy the Entra connection",
    [CONNECT_METHOD.MANAGED]: "Grant admin consent",
    [CONNECT_METHOD.BYO]: "Add the permissions and paste the app registration",
};

function ConnectSteps({ directory, connections, onCreated }) {
    const [method, setMethod] = useState(null);
    const directories = directory?.loaded
        ? connectedEntraDirectories(directory.data).map((candidate) => ({
              ...candidate,
              copilotConnected: copilotHoldsTenant(connections, candidate.tenantId),
          }))
        : null;
    const hasEntra = (directories?.length ?? 0) > 0;
    const allCopied = hasEntra && directories.every((candidate) => candidate.copilotConnected);

    return (
        <Steps label="Connect Microsoft 365 Copilot">
            <Step
                number={1}
                state={method ? "done" : "current"}
                title="Choose how Origin connects to Microsoft"
            >
                <div aria-busy={directories === null ? "true" : undefined}>
                    <Panel className="grid gap-0">
                        {hasEntra ? (
                            <MethodOption
                                name="copilot-connect-method"
                                selected={method === CONNECT_METHOD.ENTRA}
                                onSelect={() => setMethod(CONNECT_METHOD.ENTRA)}
                                title="Use your Microsoft Entra connection"
                                description="Reuses the Microsoft app and admin consent your Entra directory connection already has."
                                disabledReason={
                                    allCopied
                                        ? directories.length === 1
                                            ? COPILOT_ALREADY_CONNECTED
                                            : "Every connected Entra directory's Microsoft tenant already has a Copilot connection."
                                        : null
                                }
                            />
                        ) : null}
                        <MethodOption
                            name="copilot-connect-method"
                            selected={method === CONNECT_METHOD.MANAGED}
                            onSelect={() => setMethod(CONNECT_METHOD.MANAGED)}
                            title="Sign in with Microsoft"
                            description="A Microsoft admin grants consent to Origin's app. Nothing to create or rotate."
                        />
                        <MethodOption
                            name="copilot-connect-method"
                            selected={method === CONNECT_METHOD.BYO}
                            onSelect={() => setMethod(CONNECT_METHOD.BYO)}
                            title="Use your own app registration"
                            description="You create the app in Entra, grant its permissions, and rotate its client secret."
                        />
                    </Panel>
                </div>
            </Step>
            <Step
                number={2}
                state={method ? "current" : "todo"}
                title={method ? METHOD_STEP_TITLE[method] : "Grant access"}
                summary={method ? null : "Starts after you choose how to connect."}
            >
                {method === CONNECT_METHOD.ENTRA && hasEntra && !allCopied ? (
                    <EntraCopyForm directories={directories} onCreated={onCreated} />
                ) : method === CONNECT_METHOD.MANAGED ? (
                    <ManagedConnectForm onCreated={onCreated} />
                ) : method === CONNECT_METHOD.BYO ? (
                    <ByoConnectForm onCreated={onCreated} />
                ) : null}
            </Step>
            <Step
                number={3}
                state="todo"
                title="Capture starts"
                summary="Origin reads Copilot activity once the connection is made."
                last
            />
        </Steps>
    );
}

function ConnectionSteps({ integration, directory, onChanged, refreshing }) {
    const caps = useMyPermissions();
    const {
        states: [, consentState, captureState],
        noDirectory,
    } = scopeCaptureStep(copilotSteps(integration), directory);
    const [error, setError] = useState(null);
    const [starting, setStarting] = useState(false);
    const [editing, setEditing] = useState(false);
    const onChangedRef = useRef(onChanged);
    onChangedRef.current = onChanged;
    const { pendingId, start, cancel } = useDirectoryOauthPopup({
        onResult: ({ status, message }) => {
            if (status === "success") onChangedRef.current();
            else setError(message || "Microsoft did not complete admin consent.");
        },
        onClosedWithoutResult: () => onChangedRef.current(),
    });
    const tenant = integration.entraTenantId;
    const lastSuccess = whenLabel(integration.lastSuccessAt);
    const delay = deliveryDelayNote(integration.deliveryMode);

    const continueConsent = useCallback(async () => {
        setStarting(true);
        setError(null);
        try {
            const redirectUri = directoryOauthRedirectUri(window.location.origin);
            const { authUrl } = await getCopilotAuthUrl({ integrationId: integration.id, redirectUri });
            if (!start({ integrationId: integration.id, provider: CONSENT_PROVIDER, redirectUri, authUrl })) {
                setError(POPUP_BLOCKED);
            }
        } catch (err) {
            cancel();
            setError(denialOrRawMessage(err, caps));
        } finally {
            setStarting(false);
        }
    }, [cancel, caps, integration.id, start]);

    const saved = useCallback(async () => {
        setEditing(false);
        await onChanged();
    }, [onChanged]);

    const pending = integration.status === "pending";

    return (
        <Steps label={`Copilot connection ${copilotConnectionName(integration) || "waiting for consent"}`}>
            <Step
                number={1}
                state={editing ? "current" : "done"}
                title={integration.managed ? "Sign in with Microsoft" : "Use your own app registration"}
                summary={
                    tenant ? (
                        <>
                            Microsoft tenant <span className="font-mono text-[color:var(--ink)]">{tenant}</span>.
                        </>
                    ) : (
                        "The Microsoft tenant is set when an admin grants consent."
                    )
                }
                action={
                    !integration.managed && !editing ? (
                        <button type="button" onClick={() => setEditing(true)} className={secondaryBtnCls}>
                            Edit connection
                        </button>
                    ) : null
                }
            >
                {editing ? (
                    <EditConnectionForm integration={integration} onSaved={saved} onCancel={() => setEditing(false)} />
                ) : null}
            </Step>
            <Step
                number={2}
                state={consentState}
                title={integration.managed ? "Grant admin consent" : "Verify the app registration"}
                summary={
                    consentState === "done"
                        ? integration.managed
                            ? "Admin consent granted."
                            : "Microsoft accepted the app registration."
                        : consentState === "failed"
                          ? copilotProblem(integration)
                          : "Waiting for a Microsoft admin to grant consent. Nothing is captured until then."
                }
                action={
                    pending && integration.managed && !pendingId ? (
                        <button type="button" onClick={continueConsent} disabled={starting} className={primaryBtnCls}>
                            {starting ? "Opening Microsoft..." : "Continue to Microsoft"}
                        </button>
                    ) : null
                }
            >
                {pending && integration.managed ? <ManagedPermissionList /> : null}
                {pendingId ? <ConsentWait onCancel={cancel} /> : null}
                <ErrorText>{error}</ErrorText>
            </Step>
            <Step
                number={3}
                state={captureState}
                title={noDirectory ? NO_DIRECTORY.label : captureState === "done" ? "Capturing" : "Capture starts"}
                summary={
                    noDirectory
                        ? NO_DIRECTORY.detail
                        : captureState === "todo"
                          ? "Starts after admin consent."
                          : captureState === "failed"
                            ? copilotProblem(integration)
                            : `${lastSuccess ? `Last successful sync ${lastSuccess}.` : "Connected. No successful sync yet."}${delay ? ` ${delay}` : ""}`
                }
                action={
                    !noDirectory && (captureState === "failed" || (captureState === "done" && !lastSuccess)) ? (
                        <button type="button" onClick={onChanged} disabled={refreshing} className={secondaryBtnCls}>
                            {refreshing ? "Checking..." : "Refresh"}
                        </button>
                    ) : null
                }
                last
            />
        </Steps>
    );
}

export default function CopilotPage({
    source,
    health,
    entry,
    directory,
    onBack,
    onChanged,
    selectedItem,
    onSelectItem,
    onOpenIdentity,
}) {
    const caps = useMyPermissions();
    const integrations = entry?.data ?? null;
    const [confirmRemove, setConfirmRemove] = useState(null);
    const [removing, setRemoving] = useState(false);
    const [error, setError] = useState(null);
    const knownIds = useRef(null);

    const selected =
        integrations && integrations.some((integration) => integration.id === selectedItem)
            ? selectedItem
            : integrations && integrations.length > 0 && selectedItem !== NEW_TAB
              ? integrations[0].id
              : NEW_TAB;
    const current = integrations?.find((integration) => integration.id === selected) ?? null;

    const created = useCallback(async () => {
        knownIds.current = new Set((integrations ?? []).map((integration) => integration.id));
        await onChanged();
    }, [integrations, onChanged]);

    useEffect(() => {
        if (!knownIds.current || !integrations) return;
        const added = integrations.find((integration) => !knownIds.current.has(integration.id));
        knownIds.current = null;
        if (added) onSelectItem(added.id);
    }, [integrations, onSelectItem]);

    const remove = useCallback(async () => {
        const target = confirmRemove;
        setConfirmRemove(null);
        if (!target) return;
        setRemoving(true);
        setError(null);
        try {
            await deleteCopilotIntegration(target.id);
            onSelectItem(null);
            await onChanged();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setRemoving(false);
        }
    }, [caps, confirmRemove, onChanged, onSelectItem]);

    if (!integrations) {
        return (
            <SourcePage source={source} health={health} onBack={onBack} backLabel="Back to data sources" busy={!entry?.loaded || entry?.loading} aside={null}>
                <SourceLoadState entry={entry} onRetry={onChanged} />
            </SourcePage>
        );
    }

    const tabs =
        integrations.length > 0 ? (
            <Tabs
                label="Microsoft tenants"
                selected={selected}
                onSelect={onSelectItem}
                items={[
                    ...integrations.map((integration) => ({
                        id: integration.id,
                        label: copilotConnectionName(integration) || "Waiting for consent",
                        mono: !integration.displayName && Boolean(integration.entraTenantId),
                        dotTone: connectionTone(integration),
                    })),
                    { id: NEW_TAB, label: "+ Connect Microsoft tenant" },
                ]}
            />
        ) : null;

    return (
        <SourcePage
            source={source}
            health={health}
            onBack={onBack}
            backLabel="Back to data sources"
            busy={entry.loading}
            tabs={tabs}
            aside={
                <>
                    <AsideSection title="What Origin does in Microsoft">
                        <p className="m-0">
                            Reads Copilot prompts and responses for your Microsoft tenant. Read-only. Each one is
                            attributed to a person and appears with other captured prompts.
                        </p>
                    </AsideSection>
                    <AsideSection title="Who is captured">
                        <p className="m-0">
                            Copilot capture also needs a directory connected from Entra, Okta or Google. The
                            directory decides who is in scope. Without one, nothing is captured.
                        </p>
                        {onOpenIdentity ? (
                            <button type="button" onClick={onOpenIdentity} className={`${linkCls} w-fit`}>
                                Identity connections
                            </button>
                        ) : null}
                    </AsideSection>
                    <AsideSection title="One connection per Microsoft tenant">
                        <p className="m-0">Connect each Microsoft tenant whose Copilot activity you want captured.</p>
                    </AsideSection>
                    {current ? (
                        <AsideSection title="Disconnect">
                            <p className="m-0">
                                Capture stops for this Microsoft tenant. Prompts already captured are kept.
                            </p>
                            <button
                                type="button"
                                onClick={() => setConfirmRemove(current)}
                                disabled={removing}
                                className={`${dangerBtnCls} w-fit`}
                            >
                                Disconnect
                            </button>
                            <ErrorText>{error}</ErrorText>
                        </AsideSection>
                    ) : null}
                </>
            }
        >
            {current ? (
                <ConnectionSteps
                    key={current.id}
                    integration={current}
                    directory={directory}
                    onChanged={onChanged}
                    refreshing={entry.loading}
                />
            ) : (
                <ConnectSteps directory={directory} connections={integrations ?? []} onCreated={created} />
            )}
            <ConfirmDialog
                open={Boolean(confirmRemove)}
                analyticsId={DIALOG.COPILOT_DISCONNECT}
                eyebrow="Microsoft 365 Copilot"
                title="Disconnect this Microsoft tenant?"
                body="Capture stops for this Microsoft tenant. Prompts already captured are kept. Reconnecting means choosing how to connect again."
                confirmLabel="Disconnect"
                destructive
                onConfirm={remove}
                onCancel={() => setConfirmRemove(null)}
            />
        </SourcePage>
    );
}
